import ListenerItem from '../../../utils/ListenerItem';
import DomEventHelper from '../../../utils/DomEventHelper';
import EventListenerManager from '../../../utils/EventListenerManager';
import ItmMgr from '../../../gui/ItmMgr';
import Validator from '../../../utils/Validator';
import XtwUtils from '../util/XtwUtils';
import { OWN_TEXT_COLOR } from './XRowItem';
import { OWN_BACKGROUND_COLOR } from './XCellItem';

export const GROUP_HEADER_CLASS = "group-header";
const ICO_COLLAPSED = '<i class="far fa-angle-right" style="vertical-align:middle;"></i>';
const ICO_EXPANDED = '<i class="far fa-angle-down" style="vertical-align:middle;"></i>';
const ICO_EMPTY = '';

/**
 * class XRowItemGroupHeaderExtension - provides the "group header" rendering and
 * expand/collapse behavior for a row item ({@link XRowItem}). It is a plain delegate: the
 * host row item is passed in and accessed via {@link #host}. It extends ListenerItem so it
 * owns its own listeners, which bind to the host row item's element.
 */
export default class XRowItemGroupHeaderExtension extends ListenerItem {

	/**
	 * constructs a new instance
	 * @param {*} host the host row item (XRowItem)
	 */
	constructor( host ) {
		super( 'widgets.xtw.parts.XRowItemGroupHeaderExtension' );
		this._host = host;
	}

	/**
	 * @returns {*} the host row item
	 */
	get host() {
		return this._host;
	}

	/**
	 * the host row item's DOM element. Exposed because this extension registers its own
	 * event listeners (via {@link ListenerItem#addListener}), which bind to this element.
	 * @returns {HTMLElement|undefined} the host's DOM element
	 */
	get element() {
		return this.host.element;
	}

	/**
	 * @returns {Boolean} true if the host row item is rendered
	 */
	get isRendered() {
		return this.host.isRendered;
	}

	/**
	 * @override
	 */
	doDestroy() {
		this._host = void 0;
		super.doDestroy();
	}

	get isGroupHead() {
		const modelItem = this.host.item;
		if ( Validator.is( modelItem, "MGroup" ) ) {
			return true;
		}
		if ( !Validator.isObject( modelItem ) ||
			!Validator.isFunction( modelItem.isGroupHead ) ) {
			return false;
		}
		return modelItem.isGroupHead();
	}

	addDblclickListener() {
		return this.addListener( "dblclick", "onDoubleClick" );
	}

	onDoubleClick( evt ) {
		if ( !this.host.isRendered || !( evt instanceof MouseEvent ) ||
			!this.isGroupHead ) {
			return false;
		}
		return this._toggleExpandedCollapsedGroupState();
	}

	_setGroupHeaderModelItem( tableHeaderWidget, modelItem, updateSelectionUi = true ) {
		const xRowItemElement = this.host.getDomElement();
		if ( xRowItemElement instanceof HTMLElement ) {
			xRowItemElement.classList.remove( 'xtwrtprowitem' );
			xRowItemElement.classList.add( GROUP_HEADER_CLASS );
		}
		if ( this.host.rtpMode ) {
			this.host._adjustRowItemHeight( xRowItemElement, modelItem );
		}
		this._setupGroupHeader( tableHeaderWidget );
	}

	/**
	 * creates / updates a group header
	 * @param {XtwHead} xth the table header widget
	 */
	_setupGroupHeader( xth ) {
		const host = this.host;
		// drop dynamic part
		host._dropCont( true, true );
		// make sure that fixed container exists
		host._creCont( null, true, false );
		EventListenerManager.addListener( {
			instance: this,
			eventName: "contextmenu",
			functionName: "onGroupHeaderContexmenu",
			callBackPrefix: "GroupHeader",
			element: this.element,
			useCapture: false
		} );
		if ( !Validator.isObjectPath( host.item, "item.ctt" ) ||
			!( host.ccnFix instanceof HTMLElement ) ) {
			return false;
		}
		host.ccnFix.classList.add( 'xtwgrouphead' );
		this._setupGroupHeaderButton();
		this._setupGroupHeaderContent();
		this.setGroupHeaderBackgroundColor( host.ccnFix, host.item.ctt.prop.bgc );
		return true;
	}

	_setupGroupHeaderButton() {
		const host = this.host;
		if ( !Validator.isObject( host.item ) ||
			!( host.ccnFix instanceof HTMLElement ) ) {
			return false;
		}
		if ( host.btnGcl instanceof HTMLElement ) {
			this.log(`The group header row item already has an` +
				` expand/collapse button. A new button will be created to replace the` +
				` old one.` );
		}
		const expandStateButton = document.createElement( 'div' );
		expandStateButton.classList.add( 'xtwgroupcollapse' );
		if ( host.item.hasChildren() ) {
			expandStateButton.addEventListener( 'click', domEvent => {
				this._onExpandClick( domEvent );
			} );
			expandStateButton.innerHTML = host.item.isCollapsed() ?
				ICO_COLLAPSED : ICO_EXPANDED;
		} else {
			expandStateButton.innerHTML = ICO_EMPTY;
		}
		host.ccnFix.appendChild( expandStateButton );
		host.btnGcl = expandStateButton;
		return true;
	}

	_setupGroupHeaderContent() {
		const host = this.host;
		if ( !Validator.isObjectPath( host.item, "item.ctt" ) ||
			!( host.ccnFix instanceof HTMLElement ) ) {
			return false;
		}
		const groupHeaderContent = document.createElement( 'div' );
		groupHeaderContent.classList.add( 'xtwgrouptext' );
		this.renderGroupHeaderContent( groupHeaderContent, host.item.ctt );
		host.ccnFix.appendChild( groupHeaderContent );
		return true;
	}

	setGroupHeaderBackgroundColor( element, color ) {
		if ( !( element instanceof HTMLElement ) ) {
			return false;
		}
		let backgroundColor = XtwUtils.colorArrayToRgba( color );
		if ( !Validator.isString( backgroundColor ) ) {
			backgroundColor = null;
		}
		element.style.setProperty( OWN_BACKGROUND_COLOR, backgroundColor );
		return true;
	}

	renderGroupHeaderContent( groupTextContainerElement, contentProperties ) {
		if ( !( groupTextContainerElement instanceof HTMLElement ) ) {
			return false;
		}
		if ( !Validator.isObject( contentProperties ) ) {
			groupTextContainerElement.innerHTML = "";
			return false;
		}
		const text = Validator.isString( contentProperties.text ) ?
			contentProperties.text : "";
		groupTextContainerElement.innerHTML = text;
		const properties = contentProperties.prop;
		const backgroundColor = Validator.isObject( properties ) ?
			properties.bgc : null;
		const rgbaBackgroundColor =
			XtwUtils.colorArrayToRgba( backgroundColor );
		if ( Validator.isString( rgbaBackgroundColor ) ) {
			groupTextContainerElement.style
				.setProperty( OWN_BACKGROUND_COLOR, rgbaBackgroundColor );
		}
		const color = Validator.isObject( properties ) ? properties.txc : null;
		const rgbaColor = XtwUtils.colorArrayToRgba( color );
		if ( Validator.isString( rgbaColor ) ) {
			groupTextContainerElement.style
				.setProperty( OWN_TEXT_COLOR, rgbaColor );
		}
		const itemManager = ItmMgr.getInst();
		if ( !Validator.is( itemManager, "ItmMgr" ) ) {
			return false;
		}
		const font = Validator.isObject( properties ) ? properties.font : null;
		itemManager.setFnt( groupTextContainerElement, font );
		return true;
	}

	onGroupHeaderContexmenu( domEvent ) {
		DomEventHelper.stopIf( domEvent );
	}

	/**
	 * handles clicks on the group expand/collapse button
	 * @param {MouseEvent} e the click event
	 */
	_onExpandClick( e ) {
		e.stopPropagation();
		e.preventDefault();
		this._toggleExpandedCollapsedGroupState();
	}

	_toggleExpandedCollapsedGroupState() {
		const modelItem = this.host.item;
		if ( !Validator.is( modelItem, "MGroup" ) ) {
			return false;
		}
		if ( !modelItem.hasChildren() ) {
			return false;
		}
		const newState = !modelItem.isCollapsed();
		// set the "collapsed" status of the group...
		modelItem.setCollapsed( newState );
		// ... and trigger the UI
		let uiTriggered = false;
		const tblBody = this.host.tblBody;
		if ( Validator.isObject( tblBody ) &&
			Validator.isFunction( tblBody.onGroupExpanded ) ) {
			tblBody.onGroupExpanded( modelItem );
			uiTriggered = true;
		}
		// ... and then, this should not be necessary any longer
		let arrowIconChanged = false;
		if ( this.host.btnGcl instanceof HTMLElement ) {
			this.host.btnGcl.innerHTML = newState ? ICO_COLLAPSED : ICO_EXPANDED;
			arrowIconChanged = true;
		}
		return uiTriggered && arrowIconChanged;
	}

}
